'use client';

import Image from 'next/image';
import Script from 'next/script';
import { useEffect, useState, useCallback } from 'react';
import * as CookieConsent from "vanilla-cookieconsent";

// Add TypeScript declarations
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (command: string, ...args: unknown[]) => void;
    FreshworksWidget: {
      q: unknown[];
      (...args: unknown[]): void;
    };
    fwSettings: {
      widget_id: number;
    };
    fbq: {
      callMethod?: boolean;
      queue?: unknown[];
      push: unknown;
      loaded: boolean;
      version: string;
      (...args: unknown[]): void;
    };
    _fbq: unknown;
    TiktokAnalyticsObject: string;
    ttq: {
      methods: string[];
      _i: Record<string, unknown>;
      instance: (id: string) => void;
      load: (id: string) => void;
      page: () => void;
      track: (event: string) => void;
    };
  }
}

interface CookieConsentInstance {
  on: (event: string, callback: () => void) => void;
  off: (event: string, callback: () => void) => void;
}

export function CookiePolicy() {
  const [advertisingConsent, setAdvertisingConsent] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [optionalConsent, setOptionalConsent] = useState(false);

  const updateConsent = useCallback(() => {
    try {
      // Get the current status from the cookie consent API
      const status = CookieConsent.getConfig('categories');
      if (!status) {
        console.log('No cookie consent status found');
        return;
      }

      const analytics = status.analytics?.enabled || false;
      const advertising = status.advertising?.enabled || false;
      const optional = status.optional?.enabled || false;

      console.log('Consent Update:', { analytics, advertising, optional });

      setAnalyticsConsent(analytics);
      setAdvertisingConsent(advertising);
      setOptionalConsent(optional);
    } catch (err) {
      console.log('Error checking consent:', err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds maximum wait
    let consentInstance: CookieConsentInstance | null = null;

    const checkConsent = async () => {
      attempts++;
      if (typeof window !== 'undefined') {
        try {
          // Check if CookieConsent is ready by accessing the config
          const config = CookieConsent.getConfig();
          if (!config) {
            throw new Error('No CookieConsent config');
          }

          console.log('CookieConsent found and initialized');
          
          // Get the instance
          const instance = await CookieConsent.run({
            // Empty config to get the instance without reinitializing
          });

          // Cast the instance to our interface type
          consentInstance = instance as unknown as CookieConsentInstance;

          if (consentInstance) {
            updateConsent();
            
            // Setup listener for consent changes
            consentInstance.on('consent', () => {
              console.log('Consent changed');
              updateConsent();
            });
          }

          return true; // Successfully initialized
        } catch {
          console.log('CookieConsent not fully initialized, retrying...', attempts);
          if (attempts < maxAttempts) {
            timeoutId = setTimeout(checkConsent, 100);
          } else {
            console.log('Max attempts reached waiting for CookieConsent');
          }
          return false;
        }
      }
      return false;
    };

    // Start checking after a short delay to allow CookieConsent to initialize
    timeoutId = setTimeout(checkConsent, 500);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (consentInstance) {
        try {
          consentInstance.off('consent', updateConsent);
        } catch (err) {
          // Ignore cleanup errors
          console.debug('Error during cleanup:', err instanceof Error ? err.message : 'Unknown error');
        }
      }
    };
  }, [updateConsent]);

  return (
    <>
      {/* Google Analytics Scripts */}
      {analyticsConsent && (
        <>
          <Script
            id="google-analytics"
            src="https://www.googletagmanager.com/gtag/js?id=AW-10895260400"
            strategy="afterInteractive"
          />
          <Script
            id="google-analytics-init"
            strategy="afterInteractive"
          >
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-10895260400');
            `}
          </Script>
        </>
      )}

      {/* TikTok Pixel Script */}
      {advertisingConsent && (
        <Script
          id="tiktok-pixel"
          strategy="afterInteractive"
        >
          {`
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
              ttq.load('CPS9E8BC77U1C0R4Q0TG');
              ttq.page();
            }(window, document, 'ttq');
          `}
        </Script>
      )}

      {/* Meta Pixel Script */}
      {advertisingConsent && (
        <Script
          id="meta-pixel"
          strategy="afterInteractive"
        >
          {`
            !function(f,b,e,v,n,t,s) {
              if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '399280452118972');
              fbq('track', 'PageView');
          `}
        </Script>
      )}

      {/* Freshdesk Widget Scripts */}
      {optionalConsent && (
        <Script
          id="freshdesk-widget-settings"
          strategy="afterInteractive"
        >
          {`
            window.fwSettings = {
              'widget_id': 154000000064
            };
            (function() {
              if (typeof window.FreshworksWidget !== 'function') {
                var n = function() {
                  n.q.push(arguments);
                };
                n.q = [];
                window.FreshworksWidget = n;
              }
            })();

            // Load the widget script after settings are defined
            function loadFreshdesk() {
              var script = document.createElement('script');
              script.src = 'https://widget.freshworks.com/widgets/151000003625.js';
              script.async = true;
              script.defer = true;
              document.body.appendChild(script);
            }
            loadFreshdesk();
          `}
        </Script>
      )}

      {/* Meta Pixel Noscript Fallback */}
      {advertisingConsent && (
        <noscript>
          <Image
            height={1}
            width={1}
            style={{ display: 'none' }}
            src="https://www.facebook.com/tr?id=399280452118972&ev=PageView&noscript=1"
            alt="Meta Pixel"
            priority
          />
        </noscript>
      )}
    </>
  );
} 